import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Div, Headline, Flex, Gutter, SearchInput, Button, Loader, ModalDialog, BodyText } from '@gaiads/telia-react-component-library';
import { useHistory, useParams } from 'react-router-dom';
import { BundlePrice, SalesChannel } from '@cdaas/api-clients/dist/product-service-openapi';

import { selectAllLocales } from '../../store/locales';
import { fetchSalesChannels, selectSalesChannelByUuid } from '../../store/salesChannels';
import BottomDrawer from './BottomDrawer';
import { Loading } from '../../utils/store';
import {
  selectAdminBundlesLoading,
  selectAdminBundlesPageMetadata,
  deleteBundles,
  fetchAdminBundlesBySalesChannelUuid,
  fetchAdminSalesChannelBundle,
  selectAllSalesChannelBundlesWithContent,
} from '../../store/adminBundles';
import BundlesList from './BundlesList/BundlesList';
import { RootState } from '../../store';
import { ListPagination } from '../../shared/ListUtil/Pagination';
import { setNotification } from '../../store/ui';
import { productServiceAdminBundlePricesClient, productServiceAdminPriceRefreshRequestsClient } from '../../api-client/product-service-client';
import { selectAllCountries } from '../../store/adminCountries';
import SalesChannelDrawer from '../Bundles/SalesChannelDrawer/SalesChannelDrawer';

import './BundlesList/Bundles.scss';
import { SalesChannelDrawerBundleInfo } from '../Bundles/SalesChannelDrawer/types';

export interface SalesChannelProductsParamTypes {
  salesChannelUuid?: string;
}

export default function SalesChannelBundlesView() {
  const { t } = useTranslation('bundlesview');
  const dispatch = useDispatch();
  const history = useHistory();
  const bundleListItems = useSelector(selectAllSalesChannelBundlesWithContent);
  const loading = useSelector(selectAdminBundlesLoading) !== Loading.SUCCEEDED;
  const pageMetadata = useSelector(selectAdminBundlesPageMetadata);
  const locales = useSelector(selectAllLocales);
  const salesChannelUuid = useParams<SalesChannelProductsParamTypes>().salesChannelUuid as SalesChannel['uuid'];
  const salesChannel = useSelector((state: RootState) => (salesChannelUuid ? selectSalesChannelByUuid(state, salesChannelUuid) : undefined));
  const countries = useSelector((state: RootState) => selectAllCountries(state));

  const numberOfItemsPerPageOptions = [20, 50, 100];

  const [searchValue, setSearchValue] = useState<string | undefined>();
  const [selectedBundles, setSelectedBundles] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
  const [numberOfItems, setNumberOfItems] = useState<number>(numberOfItemsPerPageOptions[0]);
  const [deleteConfirmationBundles, setDeleteConfirmationBundles] = useState<boolean>(false);
  const [deleteBundleFromSalesChannelConfirmationIsOpen, setDeleteBundleFromSalesChannelConfirmationIsOpen] = useState<boolean>(false);
  const [openedBundles, setOpenedBundles] = useState<number[]>([]);
  const [loadedBundles, setLoadedBundles] = useState<number[]>([]);
  const [currency, setCurrency] = useState<string>();
  const [showSalesChannelDrawer, setShowSalesChannelDrawer] = useState<boolean>(false);

  useEffect(() => {
    dispatch(fetchSalesChannels());
  }, [dispatch, currentPageNumber, numberOfItems]);

  const refreshList = () => {
    return fetchAdminBundlesBySalesChannelUuid({
      salesChannelUuid,
      goToPageNumber: currentPageNumber,
      numberOfItems: numberOfItems,
      searchText: searchValue,
    });
  };

  useEffect(() => {
    setLoadedBundles([]);
    setOpenedBundles([]);
    dispatch(refreshList());
    const salesChannelCountry = countries.find(country => country.id === salesChannel?.country_id);
    setCurrency(salesChannelCountry && salesChannelCountry.currency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesChannelUuid, currentPageNumber, numberOfItems]);

  const onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void = event => {
    let searchText: string | undefined = event.target.value;
    if (!event.target.value || event.target.value.length === 0) {
      searchText = undefined;
    }
    setSearchValue(searchText);
  };

  const onSearchClick = async () => {
    setCurrentPageNumber(0);
    dispatch(
      fetchAdminBundlesBySalesChannelUuid({
        salesChannelUuid,
        goToPageNumber: 0,
        numberOfItems: numberOfItems,
        searchText: searchValue,
      }),
    );
  };

  const handleDeleteBundles = async () => {
    setDeleteConfirmationBundles(false);
    if (selectedBundles.length === 0) {
      return;
    }

    dispatch(deleteBundles(selectedBundles));
  };

  const resetSelections = () => {
    setSelectedBundles([]);
    setSelectAll(false);
  };

  /* Checkbox event handlers */

  const handleAllChecked = () => {
    const updatedSelectAll = !selectAll;
    let updatedSelection: number[] = [];

    if (updatedSelectAll) {
      const bundleListItemsIds = bundleListItems.map(item => item.id);
      updatedSelection = [...bundleListItemsIds];
    }

    setSelectAll(updatedSelectAll);
    setSelectedBundles(updatedSelection);
  };

  // Move to bundle detail view
  const moveToBundleDetail = (bundleId: number) => {
    history.push(`/bundle/${bundleId}`);
  };

  const isOpened = (bundleId: number): boolean => openedBundles.some(existingBundleId => existingBundleId === bundleId);

  const isSelected = (bundleId: number): boolean => selectedBundles.some(existingBundleId => existingBundleId === bundleId);

  const isLoaded = (bundleId: number): boolean => loadedBundles.some(existingBundleId => existingBundleId === bundleId);

  const handleBundleChecked = (bundleId: number) => {
    let updateSelected = [...selectedBundles];
    if (isSelected(bundleId)) {
      updateSelected = updateSelected.filter(selectedBundleId => selectedBundleId !== bundleId);
    } else {
      updateSelected.push(bundleId);
    }
    setSelectedBundles(updateSelected);
  };

  // Open or close product's variants section
  const handleOpenRow = async (bundleId: number) => {
    let updateOpened = [...openedBundles];
    if (isOpened(bundleId)) {
      updateOpened = updateOpened.filter(openBundleId => openBundleId !== bundleId);
    } else {
      updateOpened.push(bundleId);
    }
    setOpenedBundles(updateOpened);
    if (!loadedBundles.includes(bundleId)) {
      if (salesChannelUuid) {
        await dispatch(fetchAdminSalesChannelBundle({ salesChannelUuid, bundleId }));
      }
      setLoadedBundles(previousLoadedBundles => [...previousLoadedBundles, bundleId]);
    }
  };

  const selectedBundlesCount = selectedBundles.length;
  const selectedBundlesWithObjects = bundleListItems.filter(bundle => selectedBundles.includes(bundle.id));
  const removeBundleFromSalesChannel = async () => {
    if (selectedBundles.length === 0 && !salesChannel) {
      return;
    }
    try {
      for (const bundle of selectedBundlesWithObjects) {
        const { id, price } = bundle;
        if (price?.id) {
          await productServiceAdminBundlePricesClient.deleteBundlePrice(id, price?.id);
        }
      }
      resetSelections();
      dispatch(
        fetchAdminBundlesBySalesChannelUuid({
          salesChannelUuid,
          goToPageNumber: currentPageNumber,
          numberOfItems: numberOfItems,
          searchText: searchValue,
        }),
      );
    } catch {
      dispatch(
        setNotification({
          text: t('notification.errorDeletingSalesChannel'),
          show: true,
          type: 'error',
        }),
      );
    }
    setDeleteBundleFromSalesChannelConfirmationIsOpen(false);
  };

  const handlePriceSave = async (bundleId: number, updatedBundlePrice: BundlePrice): Promise<void> => {
    dispatch(
      setNotification({
        text: '',
        show: false,
        type: 'success',
      }),
    );

    if (bundleId && updatedBundlePrice && salesChannel) {
      const bundle = bundleListItems.find(bundle => bundle.id === bundleId);
      if (bundle && bundle.price) {
        try {
          await productServiceAdminBundlePricesClient.putBundlePrice(bundle.id, bundle.price?.id, updatedBundlePrice);
        } catch {
          dispatch(
            setNotification({
              text: t('notification.errorUpdatingPrice'),
              show: true,
              type: 'error',
            }),
          );
        }
      }
    }
  };

  const handleUpdatePrices = async (bundleIds?: number[]): Promise<void> => {
    try {
      await productServiceAdminPriceRefreshRequestsClient.postTeliaFinancePriceRefreshRequest({
        sales_channel_uuid: salesChannelUuid || '',
        bundle_ids: bundleIds,
      });
      const successNotificationText = bundleIds
        ? t('notification.refreshSelectedPricesSuccessful', { salesChannelName: salesChannel?.name, count: bundleIds.length })
        : t('notification.refreshAllPricesSuccessful', { salesChannelName: salesChannel?.name });
      dispatch(
        setNotification({
          text: successNotificationText,
          show: true,
          type: 'success',
        }),
      );
    } catch {
      dispatch(
        setNotification({
          text: t('notification.refreshPricesFailed'),
          show: true,
          type: 'error',
        }),
      );
    }
  };

  const selectedListItem: SalesChannelDrawerBundleInfo[] = useMemo(
    () =>
      bundleListItems.filter(bundle => bundle.id === selectedBundles[0]).map(bundle => ({ name: bundle.name, brand: bundle.brand, id: bundle.id })),
    [bundleListItems, selectedBundles],
  );

  return (
    <Div margin={{ horizontal: 'xxlg', vertical: 'xlg' }}>
      <Flex spaceBetween>
        <Headline size={'md'} padding={{ horizontal: 'xxxs' }}>
          {`${salesChannel?.name} > ${t('header_title')}`}
        </Headline>
        <Flex>
          {salesChannel?.use_automatic_lease_price && (
            <Button variant="secondary" size="md" onClick={() => handleUpdatePrices()} margin={{ right: 'md' }}>
              {t('refresh_prices_button')}
            </Button>
          )}
          <Button variant="primary" size="md" onClick={() => history.push('/bundle')}>
            {`+ ${t('add_bundle')}`}
          </Button>
        </Flex>
      </Flex>
      <Gutter size={'xlg'} />
      <Div>
        <Flex>
          <Div flexItem={{ sizeMultiplier: 2, alignSelfTo: 'bottom' }} padding={{ horizontal: 'xxxs' }}>
            <SearchInput placeholder={t('filter.textsearchplaceholder')} value={searchValue} onChange={onSearchChange} onIconClick={onSearchClick} />
          </Div>
          <Div flexItem={{ sizeMultiplier: 1 }} padding={{ horizontal: 'md' }}></Div>
          <Div flexItem={{ sizeMultiplier: 4, alignSelfTo: 'bottom' }} padding={{ horizontal: 'xxxs' }}></Div>
        </Flex>
        <Gutter size="xlg" />
        <Div padding={{ horizontal: 'xxxs' }}>
          {loading ? (
            <Div alignTextToCenter padding={{ top: 'xlg' }}>
              <Loader size="lg" />
            </Div>
          ) : (
            <BundlesList
              bundleListItems={bundleListItems}
              currency={currency}
              locales={locales}
              selectAll={selectAll}
              onBundleChecked={handleBundleChecked}
              onAllChecked={handleAllChecked}
              isSelected={isSelected}
              onClickRow={moveToBundleDetail}
              isOpened={isOpened}
              isLoaded={isLoaded}
              onOpenRow={handleOpenRow}
              handlePriceSave={handlePriceSave}
              salesChannelUsesDynamicPricing={salesChannel?.use_automatic_lease_price ?? true}
            />
          )}
        </Div>
        <Gutter size="lg" />
        {!loading && (
          <ListPagination
            pageMetadata={pageMetadata}
            showText={t('pagination.show')}
            numberOfItemsPerPageOptions={numberOfItemsPerPageOptions}
            currentPageNumber={currentPageNumber}
            onPageNumberChange={setCurrentPageNumber}
            numberOfItems={numberOfItems}
            onNumberOfItemsChange={setNumberOfItems}
          />
        )}
      </Div>
      {/*selectedBundles.length > 0 && <BottomDrawer<number> selectedItems={selectedBundles}
        handleEditClick={moveToBundleDetail}
        handleDeleteClick={() => setDeleteConfirmationBundles(true)}
        handleCancelClick={resetSelections}
        editButtonText={t('button.edit_bundle')}
        selectedText={t('selected.bundleSelected', { count: selectedBundlesCount })}
        handleAddClick={()=>handleAddToSalesChannelClick()}
      />*/}
      {selectedBundles.length > 0 && (
        <BottomDrawer
          selectedBundles={selectedBundles}
          handleRemoveFromSalesChannel={() => setDeleteBundleFromSalesChannelConfirmationIsOpen(true)}
          resetSelections={resetSelections}
          handleDeleteBundle={() => setDeleteConfirmationBundles(true)}
          handleUpdatePrices={bundleIds => handleUpdatePrices(bundleIds)}
          handleEditSalesChannels={() => setShowSalesChannelDrawer(true)}
          handleEditBundle={bundleId => moveToBundleDetail(bundleId)}
        />
      )}
      {showSalesChannelDrawer && (
        <SalesChannelDrawer
          selectedItems={selectedListItem}
          onCloseClick={() => setShowSalesChannelDrawer(false)}
          defaultSalesChannelUuid={salesChannelUuid}
          refreshList={refreshList}
        />
      )}
      <ModalDialog
        title={t('delete_bundle_confirmation.title', { count: selectedBundlesCount })}
        closeButtonText={t('delete_bundle_confirmation.buttons.cancel')}
        acceptButtonText={t('delete_bundle_confirmation.buttons.delete')}
        isOpen={deleteConfirmationBundles}
        onCloseClick={() => setDeleteConfirmationBundles(false)}
        onAcceptClick={() => handleDeleteBundles()}
      >
        <BodyText>{t('delete_bundle_confirmation.content', { count: selectedBundlesCount })}</BodyText>
      </ModalDialog>
      <ModalDialog
        title={t('notification.removeFromChannel')}
        closeButtonText={t('delete_bundle_confirmation.buttons.cancel')}
        acceptButtonText={t('delete_bundle_confirmation.buttons.accept')}
        isOpen={deleteBundleFromSalesChannelConfirmationIsOpen}
        onCloseClick={() => setDeleteBundleFromSalesChannelConfirmationIsOpen(false)}
        onAcceptClick={() => removeBundleFromSalesChannel()}
      >
        <BodyText>
          {selectedBundlesCount > 1
            ? t('notification.removeFromChannelMultiple', {
                bundlesCount: selectedBundlesCount,
                salesChannelName: salesChannel?.name || '',
                interpolation: { escapeValue: false },
              })
            : t('notification.removeFromChannelLong', {
                bundleName: selectedBundlesWithObjects[0]?.name,
                salesChannelName: salesChannel?.name || '',
                interpolation: { escapeValue: false },
              })}
        </BodyText>
      </ModalDialog>
    </Div>
  );
}
